/**
 * Returns `true` when advanced theming v2 should be active for the current
 * hub render, `false` otherwise.
 *
 * Advanced theming is gated by **both** of:
 *
 *   1. The product-level flag, exposed via `useHubContext().featureFlags
 *      ['advanced-theming-in-hub']`. The product (e.g. help-center) sets this
 *      `true` only for surfaces it has opted in (today: JSM Help Hubs only).
 *   2. The platform-level Statsig gate `hub-advanced-theming`, owned by the
 *      hubs platform team to control rollout independently of the product.
 *
 * AND-gating both lets either side hold the rollout independently — the
 * product can still keep the feature off for itself even if the platform
 * gate flips on, and vice versa. Pure read of context + statsig — SSR-safe.
 *
 * Cross-host safety: this hook tolerates a missing `<HubContextProvider>` and
 * returns `false` in that case. Confluence Company Hub and similar surfaces
 * that don't mount `HubBootstrap` (and therefore don't mount `HubContext`)
 * cannot be in a theming-enabled tree by definition, so returning `false`
 * is both safe and correct.
 */
import { useContext } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { HubContext } from '../../utils';

export const useIsAdvancedThemingEnabled = (): boolean => {
	const ctx = useContext(HubContext);
	if (!ctx) {
		// No HubContextProvider in tree (e.g. Confluence Company Hub). Theming
		// is help-center-only — return false without throwing.
		return false;
	}
	const productFlag = ctx.featureFlags?.['advanced-theming-in-hub'] === true;
	return productFlag && fg('hub-advanced-theming');
};
